import { Button, Group, Modal, Select, Stack, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useUpdateImportBeat } from 'hooks/importBeats'
import { useAppTranslation } from 'hooks/useTranslation'
import { ImportBeatRow } from 'supabase/importBeats'
import { areaLabels } from './areaLabels'
import { AreaCity, buildAreaOptions, decodeArea, encodeArea } from './beatArea'
import { mapBeatError } from './beatErrors'

type ChangeAreaModalProps = {
  beat: ImportBeatRow
  opened: boolean
  onClose: () => void
  cities: AreaCity[]
}

type ChangeAreaValues = { area: string | null }

/**
 * Change a beat's area (BEAT-02, D-13): the same municipality-or-town picker
 * as the add flow, preselected with the beat's current area. Only the area pair
 * is written; the URL and handle are not editable (D-16).
 */
export const ChangeAreaModal = ({ beat, opened, onClose, cities }: ChangeAreaModalProps) => {
  const { t } = useAppTranslation()
  const { setArea } = useUpdateImportBeat()

  const form = useForm<ChangeAreaValues>({
    initialValues: { area: encodeArea(beat) }
  })

  const canSave = decodeArea(form.values.area) !== null

  const submit = async (values: ChangeAreaValues) => {
    const area = decodeArea(values.area)
    if (!area) return

    try {
      await setArea.mutateAsync({ handle: beat.handle, area })
      notifications.show({
        title: t('common.success'),
        message: t('beats.updated'),
        color: 'green'
      })
      onClose()
    } catch (err) {
      const m = mapBeatError(err)
      form.setErrors({ [m.field === 'area' ? 'area' : 'general']: t(m.messageKey) })
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${t('beats.changeArea')}: ${beat.handle}`}
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack>
          <Select
            label={t('beats.areaLabel')}
            placeholder={t('beats.areaPlaceholder')}
            data={buildAreaOptions(cities, areaLabels(t))}
            searchable
            required
            nothingFoundMessage={t('common.nothing_found')}
            {...form.getInputProps('area')}
          />

          {form.errors.general && (
            <Text size="sm" c="red">
              {form.errors.general}
            </Text>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave} loading={setArea.isPending}>
              {t('beats.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
